import { NgClass } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  signal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  checkBridgeConfigConflicts,
  Practice,
  type IBridgeConfigConflictReport,
  type IBridgeDeviceIntegrationSummary,
} from '@principle-theorem/principle-core';
import {
  type IPractice,
  BridgeDeviceStatus,
} from '@principle-theorem/principle-core/interfaces';
import { Firestore, type WithRef } from '@principle-theorem/shared';

@Component({
  selector: 'pt-bridge-config-conflicts',
  templateUrl: './config-conflicts.component.html',
  styleUrls: ['./config-conflicts.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButtonModule,
    MatIconModule,
    MatListModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatTooltipModule,
    NgClass,
  ],
})
export class BridgeConfigConflictsComponent {
  practice = input.required<WithRef<IPractice>>();
  readonly BridgeDeviceStatus = BridgeDeviceStatus;
  readonly displayedColumns = ['status', 'name', 'integrations'];

  loading = signal(true);
  error = signal<string | undefined>(undefined);
  report = signal<IBridgeConfigConflictReport | undefined>(undefined);

  sortedSummaries = computed(() => {
    const report = this.report();
    if (!report) {
      return [];
    }
    return [...report.integrationSummaries].sort((a, b) => {
      if (a.deleted !== b.deleted) {
        return a.deleted ? 1 : -1;
      }
      return a.deviceName.localeCompare(b.deviceName);
    });
  });

  constructor() {
    afterNextRender(() => this._loadReport());
  }

  getStatusIcon(summary: IBridgeDeviceIntegrationSummary): string {
    return summary.status === BridgeDeviceStatus.Active
      ? 'check_circle'
      : 'cancel';
  }

  getStatusLabel(summary: IBridgeDeviceIntegrationSummary): string {
    return summary.status === BridgeDeviceStatus.Active ? 'Active' : 'Inactive';
  }

  async refresh(): Promise<void> {
    this.loading.set(true);
    this.error.set(undefined);
    this.report.set(undefined);
    await this._loadReport();
  }

  private async _loadReport(): Promise<void> {
    try {
      const devices = await Firestore.getDocs(
        Practice.bridgeDeviceCol(this.practice())
      );
      const report = checkBridgeConfigConflicts(devices);
      this.report.set(report);
    } catch {
      this.error.set('Failed to load bridge device configurations');
    } finally {
      this.loading.set(false);
    }
  }
}
